// Anonymous per-tab analytics session ID.
//
// Lets BigQuery stitch events from one sitting (forms per session, session
// length, within-sitting drop-off) without any user linkage beyond the
// envelope's existing actor_id: the ID is a random UUID minted per browser
// tab, carried in sessionStorage (cleared when the tab closes), and never
// derived from anything identifying.
//
// Storage access is wrapped: private browsing modes can throw on
// sessionStorage, in which case the ID lives for the page's lifetime only.

const STORAGE_KEY = 'td.analytics.sid.v1';
const STARTED_KEY = 'td.analytics.sidstart.v1';

let inMemoryId = null;
let inMemoryStarted = false;

const mintId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function getAnalyticsSessionId() {
  try {
    const existing = sessionStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const minted = mintId();
    sessionStorage.setItem(STORAGE_KEY, minted);
    return minted;
  } catch (_) {
    if (!inMemoryId) inMemoryId = mintId();
    return inMemoryId;
  }
}

// Claims the right to emit session_started, exactly once per session. The flag
// lives beside the session ID rather than in memory so that a page reload
// inside the same tab does not produce a second start for one session_id —
// otherwise every session-scoped denominator would be inflated by reloads.
// Returns true to the first caller only.
export function claimSessionStart() {
  try {
    if (sessionStorage.getItem(STARTED_KEY)) return false;
    sessionStorage.setItem(STARTED_KEY, '1');
    return true;
  } catch (_) {
    if (inMemoryStarted) return false;
    inMemoryStarted = true;
    return true;
  }
}

// Gives the claim back, so the next caller of claimSessionStart can re-attempt
// session_started under the same session_id. Used when the attempt it was spent
// on can never land (the batch carrying the event was dropped for good): the
// claim exists to stop reloads double-counting a *delivered* start, not to
// spend a session's only attempt on a row that never arrived.
export function releaseSessionStartClaim() {
  inMemoryStarted = false;
  try {
    sessionStorage.removeItem(STARTED_KEY);
  } catch (_) {
    // The in-memory fallback above is the whole state in this case.
  }
}

// Test-only: reset the in-memory fallback between tests.
export function __resetAnalyticsSessionForTests() {
  inMemoryId = null;
  inMemoryStarted = false;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STARTED_KEY);
  } catch (_) {
    // ignore
  }
}
